import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  TextField,
  Grid,
  Container,
  Snackbar,
  IconButton,
  LinearProgress,
  Chip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Save,
  CheckCircle,
  Warning,
  Visibility,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import apiAxiosInstance from '../../utils/axiosInstance';
import { useTeacher } from '../../context/TeacherContext';

// Remove leading A-D label only when it's standalone (option label) and next word is capitalized
const stripLeadingChoiceLetter = (text: string): string => {
  if (!text) return '';
  const match = text.match(/^([A-D])([\.\)\]:\-])?\s+(.+)$/);
  if (!match) return text;
  const next = match[3] || '';
  const firstChar = next.trimStart().charAt(0);
  const isUpperAlpha = !!firstChar && firstChar >= 'A' && firstChar <= 'Z';
  return isUpperAlpha ? next.trimStart() : text;
};

export interface Question {
  _id: string;
  question: string;
  answers: string[];
  correctAnswer: string;
  explanation: string;
  // Legacy free-text standard reference (existing Test-document questions).
  standardReference?: string;
  // Phase 4 direct skill FK (placement/practice/recheck items) - see
  // db_backend/models/AssessmentPassage.js's standardRefId and
  // db_backend/api/{placement,practice}.js's serialized item shape.
  standardRefId?: string;
  bloomsLevel: string;
  answerType?: 'multiple_choice' | 'text';
}

interface QuestionWithContext extends Question {
  passageIndex: number;
  questionType: 'main' | 'passage2' | 'combined';
  passageTitle: string;
  globalIndex: number;
  questionAnswerType?: 'multiple_choice' | 'text';
}

interface Passage {
  _id: string;
  title: string;
  genre: string;
  lexileLevel: string;
  questions: Question[];
  isAIGenerated: boolean;
  includeAnswerKey: boolean;
  isPairedPassage?: boolean;
  passages: Array<{
    topic: string;
    content: string;
    genre?: string;
    lexileLevel?: string;
    questions?: Question[];
  }>;
  passage2Questions?: Question[];
  combinedQuestions?: Question[];
  passage?: string;
}

interface Test {
  _id: string;
  title: string;
  description: string;
  passages: Passage[];
  totalQuestions: number;
}

interface StudentInfo {
  firstName: string;
  lastName: string;
}

interface TestProgress {
  currentQuestionIndex: number;
  answers: Record<string, string>;
  studentInfo: StudentInfo;
  lastSaved: string;
}

// Phase 4 (homeschool core learning loop) reuse props. All optional and
// default to the original test-delivery behavior - a plain `<TakeTest />`
// with no props is byte-identical to pre-Phase-4 behavior.
export interface TakeTestProps {
  /** 'test' (default) = existing Test-document-backed flow. The other three
   *  skip fetching a Test document entirely and render `items` instead. */
  mode?: 'test' | 'placement' | 'practice' | 'recheck';
  /** Pre-generated, pre-validated items to deliver directly (placement/practice/recheck). */
  items?: Question[];
  /** Title shown in place of a fetched Test's title when `items` is provided. */
  itemsTitle?: string;
  /** Called with the learner's answers on submit instead of POSTing to
   *  /api/tests/:testId/submit and navigating to the grading page. Required
   *  whenever `items` is provided - the caller owns grading/evidence for
   *  placement, practice, and recheck (see db_backend/api/{placement,practice}.js). */
  onSubmitComplete?: (answers: Record<string, string>) => void | Promise<void>;
  /** Skip the passage panel and use a single-column layout - placement/practice/recheck
   *  items have no passage. */
  skipPassages?: boolean;
  /** Skip the student first/last name entry and progress save/load. Set for
   *  authenticated learner flows where the caller already knows who's answering. */
  hideStudentInfo?: boolean;
}

const TakeTest: React.FC<TakeTestProps> = ({
  mode = 'test',
  items,
  itemsTitle,
  onSubmitComplete,
  skipPassages,
  hideStudentInfo,
}) => {
  const { testId, publicId } = useParams<{ testId?: string; publicId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { teacher } = useTeacher();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // itemsMode: delivering a pre-generated item set (placement/practice/recheck)
  // rather than fetching a Test document. Computed from `items` presence, not
  // `mode` alone, so a caller can't accidentally trigger it without items.
  const itemsMode = mode !== 'test' && Array.isArray(items);
  const effectiveSkipPassages = skipPassages ?? itemsMode;
  const effectiveHideStudentInfo = hideStudentInfo ?? itemsMode;
  const [test, setTest] = useState<Test | null>(null);
  // Stays true (for both flows) until an effect below explicitly clears it -
  // itemsMode populates `test`/`allQuestions` and clears loading in the same
  // effect, so there's no render frame where `test` is null but loading is false.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [studentInfo, setStudentInfo] = useState<StudentInfo>({
    firstName: '',
    lastName: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // New navigation state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [allQuestions, setAllQuestions] = useState<QuestionWithContext[]>([]);
  const [showReviewScreen, setShowReviewScreen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [checkingSubmission, setCheckingSubmission] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [resettingSubmission, setResettingSubmission] = useState(false);
  const [reEnablingSubmission, setReEnablingSubmission] = useState(false);

  // Determine if we're in teacher review mode (teacher viewing /test/:id route)
  const reviewMode = (!!teacher || isTeacher) && !!testId && location.pathname.startsWith('/test/');

  // Helpers
  const stripDuplicateTitle = (title: string, content?: string): string => {
    if (!content) return '';
    const lines = content.split('\n');
    let idx = 0;
    while (idx < lines.length && lines[idx].trim() === '') idx++;
    if (idx < lines.length) {
      const first = lines[idx].trim().replace(/^#+\s*/, '');
      if (title && first.toLowerCase() === title.trim().toLowerCase()) {
        return lines.slice(idx + 1).join('\n').trimStart();
      }
    }
    return content;
  };

  // Local wrapper not needed; use top-level stripLeadingChoiceLetter

  // Question editor state (review mode)
  type EditorMode = 'edit' | 'add';
  const emptyDraft = {
    question: '',
    answers: [''],
    correctAnswer: 'A',
    answerType: 'multiple_choice' as 'multiple_choice' | 'text'
  };
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('edit');
  const [questionDraft, setQuestionDraft] = useState<typeof emptyDraft>(emptyDraft);
  const [savingEdit, setSavingEdit] = useState(false);

  // Helper accessors defined below; avoid using before definition
  let currentQuestion: QuestionWithContext | null = null;

  const openEditor = (mode: EditorMode) => {
    if (!reviewMode) return;
    setEditorMode(mode);
    const cq = getCurrentQuestion();
    if (mode === 'edit' && cq) {
      const initialAnswers = (cq.answers || []).map((a: any) => stripLeadingChoiceLetter(String(a)));
      setQuestionDraft({
        question: cq.question,
        answers: initialAnswers.length > 0 ? initialAnswers : [''],
        correctAnswer: cq.correctAnswer || 'A',
        answerType: cq.answerType || 'multiple_choice'
      });
    } else {
      setQuestionDraft(emptyDraft);
    }
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
  };

  const updateDraftAnswer = (idx: number, value: string) => {
    setQuestionDraft(prev => {
      const next = [...prev.answers];
      next[idx] = value;
      return { ...prev, answers: next };
    });
  };

  const addDraftChoice = () => {
    setQuestionDraft(prev => ({ ...prev, answers: [...prev.answers, ''] }));
  };

  const removeDraftChoice = (idx: number) => {
    setQuestionDraft(prev => ({ ...prev, answers: prev.answers.filter((_, i) => i !== idx) }));
  };

  const persistPassageQuestions = async (
    passageId: string,
    field: 'questions' | 'passage2Questions' | 'combinedQuestions',
    updated: any[]
  ) => {
    await apiAxiosInstance.put(`/api/assessment-passages/${passageId}`, { [field]: updated });
  };

  const refreshTest = async () => {
    if (!testId) return;
    try {
      const response = await apiAxiosInstance.get(`/api/tests/${testId}/public`);
      const testData = response.data;
      const transformedPassages = testData.passages.map((p: any) => ({
        ...p,
        passage2Questions: p.passage2Questions || [],
        combinedQuestions: p.combinedQuestions || []
      }));
      const testWithTransformedPassages = { ...testData, passages: transformedPassages };
      setTest(testWithTransformedPassages);
      setAllQuestions(flattenQuestions(transformedPassages));
    } catch (e) {
      console.error('Failed to refresh test after edit:', e);
    }
  };

  const saveQuestion = async () => {
    if (!test || savingEdit) return;
    const cq = getCurrentQuestion();
    const target: QuestionWithContext | null = editorMode === 'edit' ? (cq || null) : (cq || allQuestions[0] || null);
    if (!target) return;

    const passageIndex = target.passageIndex;
    const passage = test.passages[passageIndex];
    const field: 'questions' | 'passage2Questions' | 'combinedQuestions' =
      target.questionType === 'passage2' ? 'passage2Questions' : target.questionType === 'combined' ? 'combinedQuestions' : 'questions';

    const existing: any[] = (passage as any)[field] || [];

    const buildQuestion = (): any => ({
      ...(editorMode === 'edit' ? { _id: (target as any)._id } : {}),
      question: questionDraft.question,
      answers: questionDraft.answerType === 'text' ? [] : questionDraft.answers,
      correctAnswer: questionDraft.answerType === 'text' ? '' : questionDraft.correctAnswer,
      explanation: (target as any)?.explanation || '',
      standardReference: (target as any)?.standardReference || '',
      bloomsLevel: (target as any)?.bloomsLevel || '',
      answerType: questionDraft.answerType
    });

    let updated: any[];
    if (editorMode === 'edit') {
      updated = existing.map((q: any) => (q._id === (target as any)._id ? buildQuestion() : q));
    } else {
      updated = [...existing, buildQuestion()];
    }

    setSavingEdit(true);
    try {
      await persistPassageQuestions((passage as any)._id, field, updated);
      await refreshTest();
      setEditorOpen(false);
    } catch (e) {
      console.error('Failed saving question:', e);
      setError('Failed to save question.');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteQuestion = async () => {
    if (!test) return;
    const cq = getCurrentQuestion();
    if (!cq) return;
    if (!window.confirm('Delete this question? This cannot be undone.')) return;

    const passageIndex = cq.passageIndex;
    const passage = test.passages[passageIndex];
    const field: 'questions' | 'passage2Questions' | 'combinedQuestions' =
      cq.questionType === 'passage2' ? 'passage2Questions' : cq.questionType === 'combined' ? 'combinedQuestions' : 'questions';

    const existing: any[] = (passage as any)[field] || [];
    const updated = existing.filter((q: any) => q._id !== (cq as any)._id);

    try {
      await persistPassageQuestions((passage as any)._id, field, updated);
      await refreshTest();
      setEditorOpen(false);
    } catch (e) {
      console.error('Failed deleting question:', e);
      setError('Failed to delete question.');
    }
  };

  // Roster and name matching state (public assignment links)
  const [roster, setRoster] = useState<Array<{ firstName: string; lastName: string; gradeLevel?: string | null }>>([]);
  const [nameStatus, setNameStatus] = useState<
    { status: 'unknown' | 'valid' | 'confirm' | 'invalid' | 'ambiguous';
      confidence?: number;
      suggestion?: { firstName: string; lastName: string; gradeLevel?: string | null };
      candidates?: Array<{ firstName: string; lastName: string; gradeLevel?: string | null; confidence: number }>
    }
  >({ status: 'unknown' });
  const [confirmedStudent, setConfirmedStudent] = useState<{ firstName: string; lastName: string; gradeLevel?: string | null } | null>(null);

  // Assignment context (public link)
  const [assignmentPublicId, setAssignmentPublicId] = useState<string | undefined>(undefined);
  const [assignmentOptions, setAssignmentOptions] = useState<{ shuffleQuestions?: boolean; showResults?: 'never' } | undefined>(undefined);
  const [classCodeRequired, setClassCodeRequired] = useState<boolean>(false);

  // Flatten all questions from all passages into a single array with context
  const flattenQuestions = (passages: Passage[]): QuestionWithContext[] => {
    const flattened: QuestionWithContext[] = [];
    let globalIndex = 0;

    passages.forEach((passage, passageIndex) => {
      const passageTitle = passage.isPairedPassage 
        ? `Paired Passage Set ${passageIndex + 1}` 
        : `Passage ${passageIndex + 1}`;

      // Main questions
      if (passage.questions) {
        passage.questions.forEach((question) => {
          flattened.push({
            ...question,
            passageIndex,
            questionType: 'main',
            passageTitle: passage.isPairedPassage ? `${passageTitle}A` : passageTitle,
            globalIndex: globalIndex++
          });
        });
      }

      // Second passage questions (for paired passages)
      if (passage.passage2Questions) {
        passage.passage2Questions.forEach((question) => {
          flattened.push({
            ...question,
            passageIndex,
            questionType: 'passage2',
            passageTitle: `${passageTitle}B`,
            globalIndex: globalIndex++
          });
        });
      }

      // Combined questions (for paired passages)
      if (passage.combinedQuestions) {
        passage.combinedQuestions.forEach((question) => {
          flattened.push({
            ...question,
            passageIndex,
            questionType: 'combined',
            passageTitle: `${passageTitle}A & ${passageTitle}B`,
            globalIndex: globalIndex++
          });
        });
      }
    });

    return flattened;
  };

  const shuffleArray = <T,>(arr: T[]): T[] => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // Load existing progress from backend
  const loadProgress = useCallback(async () => {
    const targetTestId = test?._id || testId;
    if (process.env.NODE_ENV === 'development') {
      console.log('loadProgress called with:', { 
        firstName: studentInfo.firstName, 
        lastName: studentInfo.lastName,
        testId: targetTestId 
      });
    }
    
    if (!studentInfo.firstName || !studentInfo.lastName) {
      if (process.env.NODE_ENV === 'development') {
        console.log('loadProgress returning early - missing student info');
      }
      return;
    }
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Making API call to load progress...');
      }
      
      if (!targetTestId) return;
      const response = await apiAxiosInstance.get(
        `/api/tests/${targetTestId}/progress`,
        { 
          params: { 
            firstName: studentInfo.firstName, 
            lastName: studentInfo.lastName 
          } 
        }
      );

      if (response.data) {
        const progress: TestProgress = response.data;
        if (process.env.NODE_ENV === 'development') {
          console.log('Progress loaded successfully:', progress);
        }
        setAnswers(progress.answers || {});
        setCurrentQuestionIndex(progress.currentQuestionIndex || 0);
        setLastSaved(new Date(progress.lastSaved));
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('No progress data in response');
        }
      }
    } catch (err: any) {
      // Progress doesn't exist yet, that's okay
      console.log('No existing progress found:', err.response?.status);
    } finally {
      setProgressLoaded(true);
    }
  }, [test?._id, testId, studentInfo.firstName, studentInfo.lastName]);

  // Save progress to backend
  const saveProgress = useCallback(async (forceImmediate = false) => {
    const targetTestId = test?._id || testId;
    if (process.env.NODE_ENV === 'development') {
      console.log('saveProgress called with:', { 
        forceImmediate, 
        hasFirstName: !!studentInfo.firstName, 
        hasLastName: !!studentInfo.lastName, 
        saving 
      });
    }
    
    // For public links, avoid saving until we have a valid confirmed match
    if ((publicId && (!studentInfo.firstName || !studentInfo.lastName || nameStatus.status !== 'valid')) || saving || !targetTestId) {
      if (process.env.NODE_ENV === 'development') {
        console.log('saveProgress returning early - missing/invalid student info or already saving');
      }
      return;
    }

    setSaving(true);
    try {
      const progressData: TestProgress = {
        currentQuestionIndex,
        answers,
        studentInfo,
        lastSaved: new Date().toISOString()
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('Making API call to save progress:', progressData);
      }

      await apiAxiosInstance.post(`/api/tests/${targetTestId}/progress`, progressData);
      setLastSaved(new Date());
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Progress saved successfully');
      }
      
      if (forceImmediate) {
        // Show success message for manual save
        setError('Progress saved successfully!');
        setTimeout(() => setError(null), 2000);
      }
    } catch (err: any) {
      console.error('Error saving progress:', err);
      if (forceImmediate) {
        setError('Error saving progress. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }, [test?._id, testId, studentInfo, currentQuestionIndex, answers, saving, publicId, nameStatus.status]);



  // Check if current user is a teacher (implement based on your auth system)
  const checkIfTeacher = useCallback(async () => {
    // Only check if we have a token (otherwise we know they're not authenticated)
    const token = localStorage.getItem('token');
    if (!token) {
      setIsTeacher(false);
      return;
    }
    
    try {
      // Use the existing teacher-profile endpoint to check if user is a teacher.
      // Parent (homeschool) accounts are also Teacher documents (role: 'parent'
      // - see models/Teacher.js), so a successful response alone isn't enough.
      const userResponse = await apiAxiosInstance.get('/auth/teacher-profile');
      setIsTeacher(!!userResponse.data && userResponse.data.role !== 'parent');
    } catch (err: any) {
      // If no auth or error, assume not a teacher
      setIsTeacher(false);
    }
  }, []);

  // Check if test has been submitted
  const checkIfSubmitted = useCallback(async () => {
    const targetTestId = test?._id || testId;
    if (!studentInfo.firstName || !studentInfo.lastName) return;
    if (!targetTestId) return;
    
    setCheckingSubmission(true);
    try {
      const checkResponse = await apiAxiosInstance.get(
        `/api/tests/${targetTestId}/submissions/check`,
        { params: { firstName: studentInfo.firstName, lastName: studentInfo.lastName } }
      );

      if (checkResponse.data.exists) {
        setIsSubmitted(true);
        if (process.env.NODE_ENV === 'development') {
          console.log('Test already submitted - enabling read-only mode');
        }
      } else {
        setIsSubmitted(false);
      }
    } catch (err: any) {
      console.error('Error checking submission status:', err);
      setIsSubmitted(false);
    } finally {
      setCheckingSubmission(false);
    }
  }, [test?._id, testId, studentInfo.firstName, studentInfo.lastName]);

  // Re-enable submission (teacher only) - keeps progress/answers
  const reEnableSubmission = useCallback(async () => {
    const targetTestId = test?._id || testId;
    if (!isTeacher || !studentInfo.firstName || !studentInfo.lastName) return;
    if (!targetTestId) return;
    
    setReEnablingSubmission(true);
    try {
      await apiAxiosInstance.delete(`/api/tests/${targetTestId}/submissions/re-enable`, {
        params: { firstName: studentInfo.firstName, lastName: studentInfo.lastName }
      });
      
      setIsSubmitted(false);
      setError('Test re-enabled successfully. Student can continue with their existing progress.');
      setTimeout(() => setError(null), 3000);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Submission re-enabled successfully');
      }
    } catch (err: any) {
      console.error('Error re-enabling submission:', err);
      setError('Error re-enabling submission. Please try again.');
    } finally {
      setReEnablingSubmission(false);
    }
  }, [test?._id, testId, studentInfo.firstName, studentInfo.lastName, isTeacher]);

  // Reset submission (teacher only) - complete fresh start
  const resetSubmission = useCallback(async () => {
    const targetTestId = test?._id || testId;
    if (!isTeacher || !studentInfo.firstName || !studentInfo.lastName) return;
    if (!targetTestId) return;
    
    setResettingSubmission(true);
    try {
      await apiAxiosInstance.delete(`/api/tests/${targetTestId}/submissions/reset`, {
        params: { firstName: studentInfo.firstName, lastName: studentInfo.lastName }
      });
      
      setIsSubmitted(false);
      // Clear all local state for fresh start
      setAnswers({});
      setCurrentQuestionIndex(0);
      setLastSaved(null);
      setError('Submission reset successfully. Student will start completely fresh.');
      setTimeout(() => setError(null), 3000);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Submission reset successfully');
      }
    } catch (err: any) {
      console.error('Error resetting submission:', err);
      setError('Error resetting submission. Please try again.');
    } finally {
      setResettingSubmission(false);
    }
  }, [test?._id, testId, studentInfo.firstName, studentInfo.lastName, isTeacher]);

  // Function to attempt loading progress when fields lose focus
  const tryLoadProgress = useCallback(() => {
    // Only attempt when we have a validated match for public links
    if (studentInfo.firstName && studentInfo.lastName && test && (!publicId || nameStatus.status === 'valid')) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Attempting to load progress on blur...');
      }
      setProgressLoaded(false); // Reset so it can try again
      loadProgress();
      checkIfSubmitted(); // Also check if already submitted
    }
  }, [studentInfo.firstName, studentInfo.lastName, test, loadProgress, checkIfSubmitted, publicId, nameStatus.status]);

  // Check if user is teacher on component mount
  useEffect(() => {
    checkIfTeacher();
  }, [checkIfTeacher]);



  useEffect(() => {
    const fetchTest = async () => {
      try {
        if (publicId) {
          console.log('Fetching assignment with publicId:', publicId);
          const response = await apiAxiosInstance.get(`/api/public-assignments/${publicId}`);
          const payload = response.data;
          setAssignmentPublicId(publicId);
          setAssignmentOptions(payload.options || undefined);
          setClassCodeRequired(!!payload.classCodeRequired);

          // Fetch roster for fuzzy matching
          try {
            const rosterRes = await apiAxiosInstance.get(`/api/public-assignments/${publicId}/roster`);
            setRoster((rosterRes.data?.roster || []) as Array<{ firstName: string; lastName: string; gradeLevel?: string | null }>);
          } catch (rosterErr: any) {
            console.warn('Unable to fetch roster for public assignment:', rosterErr?.response?.status || rosterErr?.message);
            setRoster([]);
          }

          const testData = payload.test;
          const transformedPassages = testData.passages.map((passage: any) => ({
            ...passage,
            passage2Questions: passage.passage2Questions || [],
            combinedQuestions: passage.combinedQuestions || []
          }));

          const testWithTransformedPassages: Test = {
            _id: testData.id,
            title: testData.title,
            description: '',
            passages: transformedPassages,
            totalQuestions: testData.totalQuestions
          };

          setTest(testWithTransformedPassages);

          let flattenedQuestions = flattenQuestions(transformedPassages);
          if (payload.options?.shuffleQuestions) {
            flattenedQuestions = shuffleArray(flattenedQuestions);
          }
          setAllQuestions(flattenedQuestions);
        } else if (testId) {
          console.log('Fetching test with ID:', testId);
          const response = await apiAxiosInstance.get(`/api/tests/${testId}/public`);
          const testData = response.data;

          const transformedPassages = testData.passages.map((passage: any) => ({
            ...passage,
            passage2Questions: passage.passage2Questions || [],
            combinedQuestions: passage.combinedQuestions || []
          }));

          const testWithTransformedPassages = {
            ...testData,
            passages: transformedPassages
          };

          setTest(testWithTransformedPassages);
          const flattenedQuestions = flattenQuestions(transformedPassages);
          setAllQuestions(flattenedQuestions);
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('Test loaded. Assignment mode:', !!publicId);
        }
      } catch (err: any) {
        console.error('Error fetching test:', err);
        const status = err?.response?.status;
        const serverMessage = err?.response?.data?.message || err?.message;
        if (publicId) {
          if (status === 410) {
            setError(serverMessage || 'This test is not available or has been archived.');
          } else if (status === 403) {
            setError(serverMessage || 'This test is not published yet.');
          } else if (status === 404) {
            setError(serverMessage || 'Test not found or needs to be published.');
          } else {
            // For any other error on public links, prefer server message or a clear availability message
            setError(serverMessage || 'This test is not available at this time.');
          }
        } else {
          // Non-public routes keep a generic error
          setError('Error loading test. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (!itemsMode && (publicId || testId)) {
      fetchTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId, testId, itemsMode]);

  // Placement/practice/recheck: populate state directly from the `items`
  // prop instead of fetching a Test document. No passages, no student-name
  // gate (caller already knows the learner), no progress save/load - these
  // are short, single-sitting sessions initiated from an authenticated page.
  useEffect(() => {
    if (!itemsMode || !items) return;
    const flattened: QuestionWithContext[] = items.map((q, idx) => ({
      ...q,
      passageIndex: 0,
      questionType: 'main',
      passageTitle: '',
      globalIndex: idx,
    }));
    setTest({
      _id: 'items-session',
      title: itemsTitle || '',
      description: '',
      passages: [],
      totalQuestions: flattened.length,
    });
    setAllQuestions(flattened);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsMode, items, itemsTitle]);

  // --- Fuzzy matching utilities and effects ---
  const normalize = (s: string) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ');

  const levenshteinDistance = (a: string, b: string): number => {
    const m = a.length;
    const n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,      // deletion
          dp[i][j - 1] + 1,      // insertion
          dp[i - 1][j - 1] + cost // substitution
        );
      }
    }
    return dp[m][n];
  };

  const similarity = (a: string, b: string): number => {
    const na = normalize(a);
    const nb = normalize(b);
    if (!na && !nb) return 1;
    const maxLen = Math.max(na.length, nb.length);
    if (maxLen === 0) return 1;
    const dist = levenshteinDistance(na, nb);
    return 1 - dist / maxLen;
  };

  const computeBestMatches = (firstName: string, lastName: string): Array<{ firstName: string; lastName: string; gradeLevel?: string | null; confidence: number }> => {
    const nf = normalize(firstName);
    const nl = normalize(lastName);
    if (!nf || !nl || roster.length === 0) return [];
    const candidates = roster.map((s) => {
      const sf = normalize(s.firstName);
      const sl = normalize(s.lastName);
      const scoreFirst = similarity(nf, sf);
      const scoreLast = similarity(nl, sl);
      // Weight last name a bit higher
      const combined = (scoreFirst * 0.45) + (scoreLast * 0.55);
      return { firstName: s.firstName, lastName: s.lastName, gradeLevel: (s as any).gradeLevel ?? null, confidence: combined };
    });
    candidates.sort((a, b) => b.confidence - a.confidence);
    return candidates;
  };

  // Re-run fuzzy matching on input changes
  useEffect(() => {
    if (!studentInfo.firstName && !studentInfo.lastName) {
      setNameStatus({ status: 'unknown' });
      setConfirmedStudent(null);
      return;
    }
    if (roster.length === 0) {
      setNameStatus({ status: 'unknown' });
      setConfirmedStudent(null);
      return;
    }
    const candidates = computeBestMatches(studentInfo.firstName, studentInfo.lastName);
    if (candidates.length === 0) {
      setNameStatus({ status: 'unknown' });
      setConfirmedStudent(null);
      return;
    }
    const top = candidates[0];
    const topScore = top.confidence;
    // Check for exact-name duplicates (ambiguity)
    const exactNameMatches = candidates.filter((c: { firstName: string; lastName: string; gradeLevel?: string | null; confidence: number }) => 
      normalize(c.firstName) === normalize(top.firstName) && 
      normalize(c.lastName) === normalize(top.lastName) && 
      c.confidence >= 0.98
    );
    if (exactNameMatches.length > 1) {
      setNameStatus({ status: 'ambiguous', candidates: exactNameMatches });
      setConfirmedStudent(null);
      return;
    }
    if (topScore >= 0.9) {
      setNameStatus({ status: 'valid', confidence: topScore, suggestion: top });
      setConfirmedStudent({ firstName: top.firstName, lastName: top.lastName, gradeLevel: top.gradeLevel });
      // Normalize studentInfo to canonical name to ensure progress/submission join
      setStudentInfo({ firstName: top.firstName, lastName: top.lastName });
      return;
    }
    if (topScore >= 0.7) {
      setNameStatus({ status: 'confirm', confidence: topScore, suggestion: top, candidates: candidates.slice(0, 3) });
      setConfirmedStudent(null);
      return;
    }
    setNameStatus({ status: 'invalid', confidence: topScore, candidates: candidates.slice(0, 3) });
    setConfirmedStudent(null);
  }, [studentInfo.firstName, studentInfo.lastName, roster]);

  // Trigger loading of progress/submission check once we have a confirmed valid name
  useEffect(() => {
    if (nameStatus.status === 'valid' && studentInfo.firstName && studentInfo.lastName) {
      tryLoadProgress();
    }
  }, [nameStatus.status]);

  const handleConfirmSuggestion = () => {
    if (nameStatus.status === 'confirm' && nameStatus.suggestion) {
      const s = nameStatus.suggestion;
      setStudentInfo({ firstName: s.firstName, lastName: s.lastName });
      setConfirmedStudent({ firstName: s.firstName, lastName: s.lastName, gradeLevel: s.gradeLevel });
      setNameStatus({ status: 'valid', confidence: nameStatus.confidence, suggestion: s });
      tryLoadProgress();
    }
  };

  const handleChooseAmbiguous = (idx: number) => {
    const list = nameStatus.candidates || [];
    if (idx >= 0 && idx < list.length) {
      const s = list[idx];
      setStudentInfo({ firstName: s.firstName, lastName: s.lastName });
      setConfirmedStudent({ firstName: s.firstName, lastName: s.lastName, gradeLevel: s.gradeLevel });
      setNameStatus({ status: 'valid', confidence: s.confidence, suggestion: s });
      tryLoadProgress();
    }
  };

  const canProceed = !!(confirmedStudent && nameStatus.status === 'valid');


  // Navigation functions
  const navigateToQuestion = (index: number) => {
    if (index >= 0 && index < allQuestions.length) {
      setCurrentQuestionIndex(index);
      setShowReviewScreen(false);
    } else if (index >= allQuestions.length) {
      setShowReviewScreen(true);
    }
  };

  const navigatePrevious = () => {
    if (showReviewScreen) {
      setCurrentQuestionIndex(allQuestions.length - 1);
      setShowReviewScreen(false);
    } else if (currentQuestionIndex > 0) {
      navigateToQuestion(currentQuestionIndex - 1);
    }
  };

  const navigateNext = () => {
    if (currentQuestionIndex < allQuestions.length - 1) {
      navigateToQuestion(currentQuestionIndex + 1);
    } else {
      setShowReviewScreen(true);
    }
  };

  const getCurrentQuestion = (): QuestionWithContext | null => {
    return allQuestions[currentQuestionIndex] || null;
  };

  // Now that helper is declared, compute currentQuestion
  currentQuestion = getCurrentQuestion();

  const getCompletionPercentage = (): number => {
    if (allQuestions.length === 0) return 0;
    const answeredCount = allQuestions.filter(q => answers[q._id]).length;
    return Math.round((answeredCount / allQuestions.length) * 100);
  };

  const isQuestionAnswered = (questionId: string): boolean => {
    const answer = answers[questionId];
    if (!answer) return false;
    
    // For text questions, check if there's actual content
    const question = allQuestions.find(q => q._id === questionId);
    if (question?.answerType === 'text') {
      return answer.trim().length > 0;
    }
    
    // For multiple choice, any answer is considered answered
    return true;
  };

  // Helper function to get the full answer text for a given answer letter
  const getAnswerText = (questionId: string, answerLetter: string): string => {
    const question = allQuestions.find(q => q._id === questionId);
    if (!question) return '';
    
    // For text questions, return the answer as-is
    if (question.answerType === 'text') {
      return answerLetter;
    }
    
    // For multiple choice, find the answer text by letter
    const answerIndex = answerLetter.charCodeAt(0) - 65; // Convert A=0, B=1, C=2, D=3
    if (answerIndex >= 0 && answerIndex < question.answers.length) {
      return question.answers[answerIndex];
    }
    
    return answerLetter; // Fallback to just the letter
  };

  const handleAnswerSelect = (questionId: string, answer: string) => {
    // Prevent answer changes if test is already submitted
    if (isSubmitted) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Answer selection blocked - test already submitted');
      }
      return;
    }

    // In review mode, allow local selection but do not save progress
    if (reviewMode) {
      setAnswers(prev => ({
        ...prev,
        [questionId]: answer
      }));
      return;
    }

    // Add logging in development to help debug
    if (process.env.NODE_ENV === 'development') {
      console.log('Selected answer:', { questionId, answer });
      console.log('Student info:', studentInfo);
      console.log('About to call saveProgress...');
    }
    
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));

    // Save immediately when answer is selected
    saveProgress(false);
  };

  const handleStudentInfoChange = (field: keyof StudentInfo) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStudentInfo(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const validateForm = () => {
    // Authenticated item-mode flows (placement/practice/recheck) already know
    // who's answering - no name gate to pass.
    if (!effectiveHideStudentInfo) {
      // First check if student info is provided
      if (!studentInfo.firstName.trim() || !studentInfo.lastName.trim()) {
        setError('Please enter your first and last name before submitting');
        return false;
      }
      // For public links, ensure valid confirmed match
      if (publicId) {
        if (!(confirmedStudent && nameStatus.status === 'valid')) {
          setError('Name not recognized. Please correct your name before proceeding.');
          return false;
        }
      }
    }

    // Then check if all questions are answered
    if (allQuestions.length > 0) {
      const hasUnansweredQuestions = allQuestions.some(question => !answers[question._id]);

      if (hasUnansweredQuestions) {
        setError('Please answer all questions before submitting');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    // In review mode, block submission and show message
    if (reviewMode) {
      setError('This is a review only. Test will not be submitted.');
      return;
    }

    if (!validateForm()) return;

    // Item-mode flows (placement/practice/recheck) delegate grading/evidence
    // entirely to the caller - see db_backend/api/{placement,practice}.js.
    // No Test document exists to POST /submit against.
    if (itemsMode || onSubmitComplete) {
      setSubmitting(true);
      try {
        await onSubmitComplete?.(answers);
      } catch (err: any) {
        setError('Error submitting. Please try again.');
        console.error('Error in onSubmitComplete:', err);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Enforce local attempt limit for assignment links
    if (assignmentPublicId && studentInfo.firstName && studentInfo.lastName) {
      const attemptsKey = `assignmentAttempts:${assignmentPublicId}:${studentInfo.firstName.trim()}:${studentInfo.lastName.trim()}`;
      const used = parseInt(localStorage.getItem(attemptsKey) || '0', 10);
      const limit = (assignmentOptions?.shuffleQuestions !== undefined ? undefined : undefined); // placeholder to avoid TS strip
      // Read actual limit if provided
      const assignmentLimit = (assignmentOptions as any)?.limit || undefined; // options doesn't include limit; kept for future
      const windowLimit = undefined; // reserved
      const effectiveLimit = (window as any)?.assignmentAttemptsLimit || undefined; // none currently
      const finalLimit = assignmentLimit || windowLimit || effectiveLimit;
      if (finalLimit && used >= finalLimit) {
        setError('You have reached the maximum number of attempts for this assignment.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const targetTestId = test?._id || testId;
      if (!targetTestId) {
        setError('Test not loaded yet.');
        return;
      }
      // Check if student has already submitted this test
      const checkResponse = await apiAxiosInstance.get(
        `/api/tests/${targetTestId}/submissions/check`,
        { params: { firstName: studentInfo.firstName, lastName: studentInfo.lastName } }
      );

      if (checkResponse.data.exists) {
        setError('You have already submitted this test');
        return;
      }

      // Submit the test with all answers
      await apiAxiosInstance.post(`/api/tests/${targetTestId}/submit`, {
        answers,
        studentInfo,
        assignmentPublicId: assignmentPublicId || undefined
      });
      if (assignmentPublicId && studentInfo.firstName && studentInfo.lastName) {
        const attemptsKey = `assignmentAttempts:${assignmentPublicId}:${studentInfo.firstName.trim()}:${studentInfo.lastName.trim()}`;
        const used = parseInt(localStorage.getItem(attemptsKey) || '0', 10);
        localStorage.setItem(attemptsKey, String(used + 1));
      }
      
      navigate(`/tests/grade/${targetTestId}`);
    } catch (err: any) {
      setError('Error submitting test. Please try again.');
      console.error('Error submitting test:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderPassage = (passage: Passage, index: number) => {
    console.log('Rendering passage:', index, {
      hasPassages: !!passage.passages,
      passagesLength: passage.passages ? passage.passages.length : 0,
      hasPassageField: !!passage.passage,
      passageKeys: Object.keys(passage)
    });
    
    // Handle cases where we have either passages array or direct passage content
    const hasValidContent = (passage.passages && passage.passages.length > 0) || passage.passage;
    
    if (!hasValidContent) {
      console.log('Passage failed validation:', passage);
      return (
        <Box sx={{ mb: 6 }}>
          <Typography color="error">
            Error: No passage content found
          </Typography>
        </Box>
      );
    }

    return (
      <Box key={passage._id} sx={{ mb: 6 }}>
        {passage.isPairedPassage && passage.passages && passage.passages.length >= 2 ? (
          <>
            <Typography variant="h4" gutterBottom color="primary" sx={{ mb: 4 }}>
              Paired Passage Set {index + 1}
            </Typography>
            
            {/* First Passage */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {passage.passages[0]?.topic || 'Untitled'}
              </Typography>
              <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap', mb: 4, lineHeight: 1.7 }}>
                <ReactMarkdown>
                  {passage.passages[0]?.content || 'No content available'}
                </ReactMarkdown>
              </Typography>
            </Box>

            {/* Second Passage */}
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {passage.passages[1]?.topic || 'Untitled'}
              </Typography>
              <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                <ReactMarkdown>
                  {passage.passages[1]?.content || 'No content available'}
                </ReactMarkdown>
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
              {(passage.passages && passage.passages[0]?.topic) || passage.title || 'Untitled'}
            </Typography>
            <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              <ReactMarkdown>
                {stripDuplicateTitle(
                  (passage.passages && passage.passages[0]?.topic) || passage.title || '',
                  (passage.passages && passage.passages[0]?.content) || passage.passage || 'No content available'
                )}
              </ReactMarkdown>
            </Typography>
          </>
        )}
      </Box>
    );
  };



  // Individual Question Group Component
  interface QuestionGroupProps {
    questions: QuestionWithContext[];
    startIndex: number;
    endIndex: number;
    totalQuestions: number;
    currentQuestionIndex: number;
    onQuestionSelect: (index: number) => void;
  }

  const QuestionGroup: React.FC<QuestionGroupProps> = React.memo(({ 
    questions, 
    startIndex, 
    endIndex, 
    totalQuestions,
    currentQuestionIndex,
    onQuestionSelect 
  }) => {
    const gridColumns = `repeat(${questions.length}, 1fr)`;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Questions {startIndex + 1}-{endIndex} of {totalQuestions}
        </Typography>
        
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: gridColumns,
            gap: 1,
            pb: 1,
            width: '100%',
          }}
        >
          {questions.map((question, relativeIndex) => {
            const absoluteIndex = startIndex + relativeIndex;
            return (
              <Button
                key={question._id}
                variant={currentQuestionIndex === absoluteIndex ? "contained" : "outlined"}
                color={
                  currentQuestionIndex === absoluteIndex 
                    ? "primary" 
                    : isQuestionAnswered(question._id) 
                      ? "success" 
                      : "warning"
                }
                onClick={() => onQuestionSelect(absoluteIndex)}
                sx={{
                  minWidth: isMobile ? '35px' : '42px',
                  height: isMobile ? '35px' : '42px',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: 'bold',
                  position: 'relative',
                  borderRadius: '8px',
                  boxShadow: currentQuestionIndex === absoluteIndex 
                    ? '0 4px 8px rgba(0,0,0,0.2)' 
                    : '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
                  },
                  // Add border for text questions
                  border: question.answerType === 'text' ? '2px solid #1976d2' : undefined,
                  borderColor: question.answerType === 'text' ? '#1976d2' : undefined
                }}
              >
                {absoluteIndex + 1}
                {!isQuestionAnswered(question._id) && currentQuestionIndex !== absoluteIndex && (
                  <Warning sx={{ 
                    position: 'absolute', 
                    top: -4, 
                    right: -4, 
                    fontSize: isMobile ? 10 : 12,
                    color: 'warning.main',
                    background: 'white',
                    borderRadius: '50%',
                    padding: '1px'
                  }} />
                )}
                {isQuestionAnswered(question._id) && currentQuestionIndex !== absoluteIndex && (
                  <CheckCircle sx={{ 
                    position: 'absolute', 
                    top: -4, 
                    right: -4, 
                    fontSize: isMobile ? 10 : 12,
                    color: 'success.main',
                    background: 'white',
                    borderRadius: '50%',
                    padding: '1px'
                  }} />
                )}
              </Button>
            );
          })}
        </Box>
      </Box>
    );
  });

  // Question navigation with group switching - fixed flickering
  const QuestionNavigationGrid: React.FC = React.memo(() => {
    const [currentGroup, setCurrentGroup] = React.useState(0);
    const questionsPerGroup = 9;
    
    const handleQuestionSelect = (index: number) => {
      if (index >= 0 && index < allQuestions.length) {
        // Update both states simultaneously to prevent flickering
        const newGroup = Math.floor(index / questionsPerGroup);
        setCurrentGroup(newGroup);
        setCurrentQuestionIndex(index);
        setShowReviewScreen(false);
      }
    };

    // Only sync group from external navigation (Previous/Next buttons)
    const questionGroup = Math.floor(currentQuestionIndex / questionsPerGroup);
    
    React.useEffect(() => {
      // Only update group if it was changed by external navigation (not by clicking question numbers)
      if (questionGroup !== currentGroup) {
        setCurrentGroup(questionGroup);
      }
    }, [questionGroup, currentGroup]);

    // Split questions into groups of 9
    const questionGroups = React.useMemo(() => {
      const groups = [];
      for (let i = 0; i < allQuestions.length; i += questionsPerGroup) {
        const endIndex = Math.min(i + questionsPerGroup, allQuestions.length);
        groups.push({
          questions: allQuestions.slice(i, endIndex),
          startIndex: i,
          endIndex: endIndex
        });
      }
      return groups;
    }, [allQuestions.length, questionsPerGroup]);

    const totalGroups = questionGroups.length;
    const currentGroupData = questionGroups[currentGroup];
    
    if (!currentGroupData) return null;
    
    return (
      <Box sx={{ mb: 3 }}>
        {/* Navigation arrows */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                     {/* Left arrow */}
           {totalGroups > 1 && currentGroup > 0 && (
             <IconButton
               onClick={() => {
                const newGroup = currentGroup - 1;
                const firstQuestionInGroup = newGroup * questionsPerGroup;
                setCurrentGroup(newGroup);
                setCurrentQuestionIndex(firstQuestionInGroup);
               }}
               sx={{
                 position: 'absolute',
                 left: -40,
                 zIndex: 2,
                 backgroundColor: 'background.paper',
                 border: '1px solid',
                 borderColor: 'divider',
                 boxShadow: 2,
                transition: 'all 0.2s ease-in-out',
                 '&:hover': { 
                  backgroundColor: 'primary.light',
                  borderColor: 'primary.main',
                  transform: 'scale(1.15)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  color: 'primary.main'
                 }
               }}
               size="medium"
             >
               <ArrowBack />
             </IconButton>
           )}
          
          {/* Current group only */}
          <Box sx={{ width: '100%' }}>
            <QuestionGroup
              questions={currentGroupData.questions}
              startIndex={currentGroupData.startIndex}
              endIndex={currentGroupData.endIndex}
              totalQuestions={allQuestions.length}
              currentQuestionIndex={currentQuestionIndex}
              onQuestionSelect={handleQuestionSelect}
            />
          </Box>
          
                     {/* Right arrow */}
           {totalGroups > 1 && currentGroup < totalGroups - 1 && (
             <IconButton
               onClick={() => {
                const newGroup = currentGroup + 1;
                const firstQuestionInGroup = newGroup * questionsPerGroup;
                setCurrentGroup(newGroup);
                setCurrentQuestionIndex(firstQuestionInGroup);
               }}
               sx={{
                 position: 'absolute',
                 right: -40,
                 zIndex: 2,
                 backgroundColor: 'background.paper',
                 border: '1px solid',
                 borderColor: 'divider',
                 boxShadow: 2,
                transition: 'all 0.2s ease-in-out',
                 '&:hover': { 
                  backgroundColor: 'primary.light',
                  borderColor: 'primary.main',
                  transform: 'scale(1.15)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  color: 'primary.main'
                 }
               }}
               size="medium"
             >
               <ArrowForward />
             </IconButton>
           )}
        </Box>
      </Box>
    );
  });

  // New component for review screen
  const ReviewScreen: React.FC = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Test Review - {test?.title}
      </Typography>
      <Typography variant="body1" sx={{ mb: 4 }}>
        Review your answers before submitting. You can click on any question number to go back and make changes.
      </Typography>
      
      {/* Question Navigation Grid */}
      <Typography variant="h6" gutterBottom>
        Question Navigation:
      </Typography>
      
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning />
          <Typography>= This symbol indicates unanswered items.</Typography>
        </Box>
      </Alert>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
          gap: 2,
          mb: 4,
          p: 2,
          border: '1px solid #ccc',
          borderRadius: 1
        }}
      >
        {allQuestions.map((question, index) => (
          <Button
            key={question._id}
            variant="outlined"
            onClick={() => navigateToQuestion(index)}
            sx={{
              minWidth: '50px',
              height: '50px',
              borderRadius: '50%',
              position: 'relative',
              backgroundColor: isQuestionAnswered(question._id) ? 'success.light' : 'warning.light',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: isQuestionAnswered(question._id) ? 'success.main' : 'warning.main',
                transform: 'scale(1.1)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }
            }}
          >
            {index + 1}
            {!isQuestionAnswered(question._id) && (
              <Warning 
                sx={{ 
                  position: 'absolute', 
                  top: -8, 
                  right: -8, 
                  fontSize: 20,
                  color: 'error.main'
                }} 
              />
            )}
          </Button>
        ))}
      </Box>

      {/* Answer Summary */}
      <Typography variant="h6" gutterBottom>
        Your Answers Summary:
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        {allQuestions.map((question, index) => {
          const answer = answers[question._id];
          const isAnswered = isQuestionAnswered(question._id);
          
          return (
            <Paper key={question._id} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: isAnswered ? 'success.main' : 'warning.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Chip 
                  label={`Q${index + 1}`}
                  color={isAnswered ? 'success' : 'warning'}
                  size="small"
                  sx={{ minWidth: '40px' }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    {question.question}
                  </Typography>
                  {isAnswered ? (
                    <Box>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Your Answer: {answer}
                      </Typography>
                      {question.answerType !== 'text' && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', ml: 1 }}>
                          {getAnswerText(question._id, answer)}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="warning.main" sx={{ fontStyle: 'italic' }}>
                      Not answered
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigateToQuestion(index)}
                  sx={{ minWidth: '80px' }}
                >
                  Review
                </Button>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {!isSubmitted ? (
      <Paper sx={{ p: 3, backgroundColor: 'success.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ minWidth: '150px' }}
          >
            {submitting ? 'Submitting...' : 'Submit Test'}
          </Button>
          <Typography variant="body1">
            Submit this test for scoring. This will end this test session. You will <strong>NOT</strong> be able to view this test again.
          </Typography>
        </Box>
      </Paper>
      ) : (
        <Paper sx={{ p: 3, backgroundColor: 'info.light' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ textAlign: 'center', flex: 1 }}>
              ✅ This test has already been submitted. You are viewing your submitted answers in read-only mode.
            </Typography>
            {isTeacher && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="info"
                  onClick={reEnableSubmission}
                  disabled={reEnablingSubmission}
                  sx={{ minWidth: '150px' }}
                >
                  {reEnablingSubmission ? 'Re-enabling...' : 'Re-enable Test'}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={resetSubmission}
                  disabled={resettingSubmission}
                  sx={{ minWidth: '150px' }}
                >
                  {resettingSubmission ? 'Resetting...' : 'Reset Test'}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading test...</Typography>
      </Box>
    );
  }

  if (!test) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Test not found'}</Alert>
      </Box>
    );
  }

  const currentPassage = currentQuestion ? test.passages[currentQuestion.passageIndex] : null;

  return (
    <Container maxWidth="xl" sx={{ py: 2, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Student Info Section - skipped for authenticated item-mode flows
          (placement/practice/recheck), where the caller already knows who's answering */}
      {!effectiveHideStudentInfo && (
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        {reviewMode && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: 'error.light',
              color: 'error.dark',
              border: '1px solid',
              borderColor: 'error.main',
              fontSize: '1.1rem',
              fontWeight: 600,
              '& .MuiAlert-icon': { color: 'error.main' }
            }}
          >
            This is a review only. Test will not be submitted.
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5">
            {test.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, minWidth: 300, alignItems: 'center' }}>
            <TextField
              size="small"
              label="First Name"
              value={studentInfo.firstName}
              onChange={handleStudentInfoChange('firstName')}
              onBlur={tryLoadProgress}
              required
              disabled={reviewMode}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="Last Name"
              value={studentInfo.lastName}
              onChange={handleStudentInfoChange('lastName')}
              onBlur={tryLoadProgress}
              required
              disabled={reviewMode}
              sx={{ flex: 1 }}
            />
            {/* Name validation indicator */}
            {publicId && (
              <Box sx={{ minWidth: 220 }}>
                {nameStatus.status === 'valid' && (
                  <Typography variant="caption" color="success.main">
                    ✅ Match found{nameStatus.suggestion ? `: ${nameStatus.suggestion.firstName} ${nameStatus.suggestion.lastName}` : ''}
                  </Typography>
                )}
                {nameStatus.status === 'confirm' && (
                  <Box>
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                      ⚠️ Did you mean {nameStatus.suggestion?.firstName} {nameStatus.suggestion?.lastName}{nameStatus.suggestion?.gradeLevel ? ` (${nameStatus.suggestion?.gradeLevel})` : ''}?
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Button size="small" variant="contained" color="warning" onClick={handleConfirmSuggestion}>Yes</Button>
                      <Button size="small" variant="text" onClick={() => setNameStatus({ status: 'invalid' })}>Try Again</Button>
                    </Box>
                  </Box>
                )}
                {nameStatus.status === 'ambiguous' && (
                  <Box>
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                      Multiple matches found. Please choose:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      {(nameStatus.candidates || []).map((c, idx) => (
                        <Button key={`${c.firstName}-${c.lastName}-${idx}`} size="small" variant="outlined" onClick={() => handleChooseAmbiguous(idx)}>
                          {c.firstName} {c.lastName}{c.gradeLevel ? ` (${c.gradeLevel})` : ''}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
                {nameStatus.status === 'invalid' && (
                  <Typography variant="caption" color="error.main">
                    ❌ Name not recognized — please check spelling
                  </Typography>
                )}
                {nameStatus.status === 'unknown' && roster.length > 0 && (studentInfo.firstName || studentInfo.lastName) && (
                  <Typography variant="caption" color="text.secondary">
                    Start typing to find your name
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
      )}

      {/* Top Navigation Bar */}
      <Paper elevation={2} sx={{ p: isMobile ? 1 : 2, mb: 2 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: isMobile ? 1 : 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={navigatePrevious}
              disabled={currentQuestionIndex === 0 && !showReviewScreen}
              size={isMobile ? "small" : "medium"}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover:not(:disabled)': { 
                  transform: 'scale(1.15)',
                  backgroundColor: 'primary.light',
                  color: 'primary.main'
                }
              }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton 
              onClick={navigateNext}
              disabled={showReviewScreen}
              size={isMobile ? "small" : "medium"}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover:not(:disabled)': { 
                  transform: 'scale(1.15)',
                  backgroundColor: 'primary.light',
                  color: 'primary.main'
                }
              }}
            >
              <ArrowForward />
            </IconButton>
            <IconButton 
              onClick={() => saveProgress(true)}
              disabled={saving}
              color={lastSaved ? "success" : "default"}
              size={isMobile ? "small" : "medium"}
            >
              <Save />
            </IconButton>
            
            {/* Review and Submit Button */}
            <Button
              variant="outlined"
              color={getCompletionPercentage() === 100 ? "success" : "primary"}
              size={isMobile ? "small" : "medium"}
              onClick={() => setShowReviewScreen(true)}
              disabled={isSubmitted || (publicId ? !canProceed : false)}
              startIcon={getCompletionPercentage() === 100 ? <CheckCircle /> : <Visibility />}
              sx={{
                ml: 1,
                minWidth: isMobile ? 'auto' : '140px',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                borderColor: getCompletionPercentage() === 100 ? 'success.main' : 'primary.main',
                color: getCompletionPercentage() === 100 ? 'success.main' : 'primary.main',
                '&:hover': {
                  borderColor: getCompletionPercentage() === 100 ? 'success.dark' : 'primary.dark',
                  backgroundColor: getCompletionPercentage() === 100 ? 'success.light' : 'primary.light',
                }
              }}
            >
              {isMobile ? 'Review' : 'Review & Submit'}
            </Button>

            {/* Review-mode editing controls */}
            {reviewMode && (
              <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size={isMobile ? "small" : "medium"}
                  onClick={() => openEditor('edit')}
                >
                  Edit Question
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size={isMobile ? "small" : "medium"}
                  onClick={() => openEditor('add')}
                  startIcon={<AddIcon />}
                >
                  Add Question
                </Button>
              </Box>
            )}
            
            {/* Teacher Controls in submitted mode */}
            {isTeacher && isSubmitted && (
              <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                <Button
                  variant="outlined"
                  color="info"
                  size={isMobile ? "small" : "medium"}
                  onClick={reEnableSubmission}
                  disabled={reEnablingSubmission}
                >
                  {reEnablingSubmission ? 'Re-enabling...' : 'Re-enable'}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  size={isMobile ? "small" : "medium"}
                  onClick={resetSubmission}
                  disabled={resettingSubmission}
                >
                  {resettingSubmission ? 'Resetting...' : 'Reset'}
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 1 : 2,
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            {!showReviewScreen && (
              <Typography variant={isMobile ? "body2" : "h6"} noWrap sx={{ whiteSpace: 'nowrap' }}>
                Question {currentQuestionIndex + 1} of {allQuestions.length}
              </Typography>
            )}
            <Chip 
              label={`${getCompletionPercentage()}% Complete`}
              color={getCompletionPercentage() === 100 ? "success" : "primary"}
              size={isMobile ? "small" : "medium"}
            />
            {isTeacher && (
              <Chip 
                label="TEACHER MODE"
                color="warning"
                size={isMobile ? "small" : "medium"}
                sx={{ ml: 1 }}
              />
            )}
            {!isMobile && (
              <Typography variant="caption" color="text.secondary">
                {saving ? 'Saving...' : lastSaved ? `Saved: ${lastSaved.toLocaleTimeString()}` : 'Not saved yet'}
              </Typography>
            )}
          </Box>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={getCompletionPercentage()} 
          sx={{ mt: 1 }}
        />
      </Paper>

      {showReviewScreen ? (
        <ReviewScreen />
      ) : (
        <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          {/* Single-column layout: placement/practice/recheck items have no passage */}
          {effectiveSkipPassages ? (
            <Grid item xs={12} sx={{ height: '100%' }}>
              <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <QuestionNavigationGrid />
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {currentQuestion && (
                    <QuestionItem
                      question={currentQuestion}
                      questionNumber={currentQuestionIndex + 1}
                      answer={answers[currentQuestion._id]}
                      onAnswerSelect={handleAnswerSelect}
                      isSubmitted={isSubmitted}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
          ) : isMobile ? (
            <>
              {/* Passage on top for mobile */}
              <Grid item xs={12} sx={{ height: '40%' }}>
                <Paper elevation={3} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {currentPassage && renderPassage(currentPassage, currentQuestion!.passageIndex)}
                  </Box>
                </Paper>
              </Grid>

              {/* Question below for mobile */}
              <Grid item xs={12} sx={{ height: '60%' }}>
                <Paper elevation={3} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <QuestionNavigationGrid />
                  
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {currentQuestion && (
                      <QuestionItem
                        question={currentQuestion}
                        questionNumber={currentQuestionIndex + 1}
                        answer={answers[currentQuestion._id]}
                        onAnswerSelect={handleAnswerSelect}
                        passageTitle={currentQuestion.passageTitle}
                        isSubmitted={isSubmitted}
                        hideChoiceLetters={reviewMode}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            </>
          ) : (
            <>
              {/* Desktop Layout - Side by side */}
              {/* Passages Section - Left Side */}
              <Grid item xs={12} md={6} sx={{ height: '100%' }}>
                <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {currentPassage && renderPassage(currentPassage, currentQuestion!.passageIndex)}
                  </Box>
                </Paper>
              </Grid>

              {/* Questions Section - Right Side */}
              <Grid item xs={12} md={6} sx={{ height: '100%' }}>
                <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <QuestionNavigationGrid />
                  
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {currentQuestion && (
                      <QuestionItem
                        question={currentQuestion}
                        questionNumber={currentQuestionIndex + 1}
                        answer={answers[currentQuestion._id]}
                        onAnswerSelect={handleAnswerSelect}
                        passageTitle={currentQuestion.passageTitle}
                        isSubmitted={isSubmitted}
                        hideChoiceLetters={reviewMode}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error}
      />

      {/* Question Editor Dialog (Review Mode) */}
      {reviewMode && (
        <Dialog open={editorOpen} onClose={closeEditor} maxWidth="md" fullWidth>
          <DialogTitle>{editorMode === 'edit' ? 'Edit Question' : 'Add Question'}</DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              label="Question"
              value={questionDraft.question}
              onChange={(e) => setQuestionDraft(prev => ({ ...prev, question: e.target.value }))}
              margin="normal"
            />

            {/* Multiple choice editor */}
            {questionDraft.answerType === 'multiple_choice' && (
              <FormControl component="fieldset" sx={{ width: '100%', mt: 1 }}>
                <RadioGroup
                  value={questionDraft.correctAnswer}
                  onChange={(e) => setQuestionDraft(prev => ({ ...prev, correctAnswer: e.target.value }))}
                >
                  {questionDraft.answers.map((ans, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <FormControlLabel value={String.fromCharCode(65 + idx)} control={<Radio />} label={''} />
                      <TextField
                        fullWidth
                        placeholder={`Choice ${idx + 1}`}
                        value={ans}
                        onChange={(e) => updateDraftAnswer(idx, e.target.value)}
                      />
                      <IconButton color="error" onClick={() => removeDraftChoice(idx)} disabled={questionDraft.answers.length <= 1}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </RadioGroup>
                <Button startIcon={<AddIcon />} onClick={addDraftChoice} sx={{ mt: 1 }}>Add Choice</Button>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions>
            {editorMode === 'edit' && (
              <Button color="error" onClick={deleteQuestion} startIcon={<DeleteIcon />}>Delete</Button>
            )}
            <Button onClick={closeEditor}>Cancel</Button>
            <Button variant="contained" onClick={saveQuestion} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save'}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

// Updated QuestionItem component with passage title
interface QuestionItemProps {
  question: Question;
  questionNumber: number;
  answer: string;
  onAnswerSelect: (questionId: string, answer: string) => void;
  passageTitle?: string;
  isSubmitted?: boolean;
  hideChoiceLetters?: boolean;
}

const QuestionItem: React.FC<QuestionItemProps> = ({
  question,
  questionNumber,
  answer,
  onAnswerSelect,
  passageTitle,
  isSubmitted = false,
  hideChoiceLetters = false
}) => {
  // Determine question type with fallback logic
  let questionType = question.answerType || 'multiple_choice';
  
  // Fallback: if no answerType is set, try to infer from the question structure
  if (!question.answerType) {
    // If the question has answers array with choices, it's multiple choice
    if (question.answers && question.answers.length > 0) {
      questionType = 'multiple_choice';
    } else {
      // If no answers array or empty answers, it's likely a text question
      questionType = 'text';
    }
  }
  
  return (
    <Box sx={{ mb: 4 }}>
      {passageTitle && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {passageTitle}
        </Typography>
      )}
      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
        {questionNumber}. {question.question}
      </Typography>
      {isSubmitted && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This test has already been submitted. Answers cannot be changed.
        </Alert>
      )}
      
      {questionType === 'text' ? (
        <TextField
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          placeholder="Type your answer here..."
          value={answer || ''}
          onChange={isSubmitted ? undefined : (e) => onAnswerSelect(question._id, e.target.value)}
          disabled={isSubmitted}
          sx={{ mt: 2 }}
        />
      ) : (
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            value={answer || ''}
            onChange={isSubmitted ? undefined : (e) => onAnswerSelect(question._id, e.target.value)}
          >
            {question.answers.map((answerText: string, answerIndex: number) => {
              const letter = String.fromCharCode(65 + answerIndex);
              return (
                <FormControlLabel
                  key={answerIndex}
                  value={letter}
                  control={<Radio disabled={isSubmitted} />}
                  disabled={isSubmitted}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                      {/* Removed leading letter to avoid showing A./B./C. when using radios */}
                      <Box 
                        component="span" 
                        sx={{ 
                          flex: 1,
                          pl: 0.5,
                          display: 'inline-block'
                        }}
                      >
                        {stripLeadingChoiceLetter(String(answerText))}
                      </Box>
                    </Box>
                  }
                  sx={{ 
                    mb: 2,
                    ml: 0,
                    '& .MuiFormControlLabel-label': {
                      width: '100%'
                    },
                    '& .MuiRadio-root': {
                      p: 0.5,
                      mr: 0.5
                    }
                  }}
                />
              );
            })}
          </RadioGroup>
        </FormControl>
      )}
    </Box>
  );
};

export default TakeTest; 